import React, {useCallback, useMemo, useState} from 'react';
import {api} from 'lib/utils/api';
import {useRouter} from 'next/dist/client/router';
import {useSelector} from 'react-redux';
import {AppState} from '../redux/reducers';
import {hasAuthorizePaymentRequirement, hasPaymentInformationRequirement} from '../utils/underwritingRequirementUtil';
import {MiscIdentificationType, RequirementStatus} from './entities';
import {useAdditionalHealth} from './useAdditionalHealth';
import {usePolicies} from './usePolicies';
import {useQuote} from './useQuote';
import {isEmcEligible} from './utils/application';
import {truthy} from './utils/truthy';
import {isValidPolicyFilter} from './utils/prosperPortal';

type PagesOrderType = {name: string; link: string; complete: boolean};

export const usePageNavigation = () => {
    const router = useRouter();
    const {data: user} = api.user.get.useQuery();
    const {data: userQuote} = useQuote();
    const {data: policies} = usePolicies();
    const {isConfirmed: additionalHealthConfirmed} = useAdditionalHealth();
    const {data: additionalPolicyData} = api.paperworks.getAdditionalPolicyInterests.useQuery(undefined, {
        enabled: !!user,
    });
    const {financialSuitability} = useSelector((state: AppState) => state.abflags);

    // Derived state
    const visibleEmc = isEmcEligible(user);
    const visibleFinancesPage = userQuote?.user_selected_category === 'VUL';
    const {verified3080} = user?.walkthrough?.userData?.experiment || {};
    const showProsperPortal = user?.policyData?.some(isValidPolicyFilter);

    // Process policy links
    const policyLinks = React.useMemo(
        () =>
            policies?.map((p: any) => ({
                verify:
                    p.status === 'pending_update' ||
                    p.requirements.every(
                        (r: any) => r.status === RequirementStatus.SUBMITTED || r.status === RequirementStatus.RECEIVED,
                    ),
            })),
        [policies],
    );

    // Generate page order based on user state
    const pagesOrder: PagesOrderType[] = useMemo(() => {
        // Common pages that appear in all flows
        const commonItems: PagesOrderType[] = [
            {name: 'Review Quote', link: '/prequalification', complete: true},
            {name: 'Verify Identity', link: '/verification', complete: !!user?.verificationComplete},
            {name: 'Additional History', link: '/additional-history', complete: additionalHealthConfirmed},
            {name: 'Verify Employment', link: '/employment', complete: !!user?.employmentVerified},
            {name: 'Beneficiaries', link: '/beneficiaries', complete: !!user?.beneficiariesConfirmed},
        ];

        // Reusable item for additional policies
        const additionalPolicyItem: PagesOrderType = {
            name: 'Additional Policies',
            link: '/additional-policies',
            complete: !!additionalPolicyData?.completedPaperwork?.additionalPolicyInterests.length,
        };

        const bankingInformationItem: PagesOrderType = {
            name: 'Banking Information',
            link: '/banking-information',
            complete: !!user?.paymentInformation,
        };

        const financesItem: PagesOrderType = {
            name: 'Finances',
            link: '/finances',
            complete: !!user?.financialSuitabilityConfirmed,
        };

        // EMC Eligible Flow
        if (visibleEmc)
            return [
                ...commonItems,
                {
                    name: 'Application Questions',
                    link: '/application-questions',
                    complete: !!user?.applicationQuestionsComplete,
                },
                {name: 'Apply', link: '/submit-application', complete: !!user?.applicationSubmittedAt},
                // Conditional finances page for VUL products when EMC eligible
                ...(financialSuitability && visibleFinancesPage ? [financesItem] : []),
                bankingInformationItem,
                {
                    name: 'Add Bank Account',
                    link: '/banking',
                    complete: !!user?.bankingComplete || !hasPaymentInformationRequirement(user?.requirements ?? []),
                },
                {
                    name: 'Authorize Payment',
                    link: '/authorize-payment',
                    complete: !!user?.paymentAuthorized || !hasAuthorizePaymentRequirement(user?.requirements ?? []),
                },
                {
                    name: 'Sign Policy Documents',
                    link: '/policy-documents',
                    complete:
                        (!!policyLinks?.length && policyLinks?.every((p: any) => p.verify)) || !policyLinks?.length,
                },
                additionalPolicyItem,
                isEmcEligible(user)
                    ? {name: 'Wrap Up', link: '/prosper-wrap-up', complete: false}
                    : {name: 'All Set', link: '/all-set', complete: false},
            ];

        // Standard Flow
        return [
            ...commonItems,
            {
                name: 'Medical Wrap-up',
                link: '/medical-wrap-up',
                complete: !!user?.medicalWrapUpConfirmed,
            },
            {name: 'Apply', link: '/submit-application', complete: !!user?.applicationSubmittedAt},
            ...(financialSuitability && visibleFinancesPage ? [financesItem] : []),
            bankingInformationItem,
            {
                name: 'Add Bank Account',
                link: '/banking',
                complete: !!user?.bankingComplete || !hasPaymentInformationRequirement(user?.requirements ?? []),
            },
            {
                name: 'Sign Policy Documents',
                link: '/policy-documents',
                complete: (!!policyLinks?.length && policyLinks?.every((p: any) => p.verify)) || !policyLinks?.length,
            },
            additionalPolicyItem,
            !visibleEmc || userQuote?.selected_product_type?.includes('VUL')
                ? {name: 'All Set', link: '/all-set', complete: false}
                : {name: 'Wrap Up', link: '/prosper-wrap-up', complete: false},
        ].filter(truthy);
    }, [
        user,
        policyLinks,
        visibleEmc,
        additionalHealthConfirmed,
        additionalPolicyData,
        financialSuitability,
        visibleFinancesPage,
    ]);

    // Find current page index
    const currentPage = useMemo(
        () => pagesOrder.findIndex((page) => page?.link === router.pathname),
        [router.pathname, pagesOrder],
    );

    // Helper function to update route
    const updateRoute = (link: string) => {
        const currentPageLink = pagesOrder[currentPage]?.link;
        router.push({pathname: link, query: {previousRoute: currentPageLink}}, link);
    };

    // Find next incomplete page
    const nextPage = useMemo(() => {
        return pagesOrder.find((page, idx) => !page?.complete && idx !== currentPage);
    }, [pagesOrder, currentPage]);

    // Find previous page
    const previousPage = useMemo(() => {
        if (currentPage !== 0) {
            return pagesOrder[currentPage - 1];
        }
        return pagesOrder[pagesOrder.length - 1];
    }, [pagesOrder, currentPage]);

    // Navigation functions
    const goToNextPage = useCallback(
        (forcedForward: boolean = false) => {
            const nextPageToGo = pagesOrder.find(
                (page, idx) => !page?.complete && (forcedForward ? idx > currentPage : idx !== currentPage),
            );
            if (showProsperPortal) {
                updateRoute('/home');
            } else if (router.query.ref === 'reviewScreen') {
                const applicationPage = pagesOrder.find((v) => v.name === 'Submit Application');
                updateRoute(applicationPage?.link || '/');
            } else if (currentPage !== pagesOrder?.length - 1) {
                nextPageToGo && updateRoute(nextPageToGo.link);
            } else if (!nextPageToGo) {
                updateRoute(pagesOrder[0].link);
            }
        },
        [showProsperPortal, currentPage, pagesOrder, router.query.ref],
    );

    const goToOTPVerification = useCallback(() => {
        if (showProsperPortal) {
            updateRoute('/home');
        } else if (
            verified3080 &&
            !user?.identifications.find(
                (i) =>
                    i.type === MiscIdentificationType.SOCIAL_SECURITY_NUMBER &&
                    JSON.parse(i.value).source === 'verified',
            ) &&
            !user?.firstIdentification?.value?.socialSecurityNumber
        ) {
            updateRoute('/phone-verification');
        } else {
            goToNextPage();
        }
    }, [goToNextPage, updateRoute, verified3080, user?.identifications, user?.firstIdentification, showProsperPortal]);

    const goToPreviousPage = useCallback(() => {
        if (router.query.ref === 'reviewScreen') {
            const applicationPage = pagesOrder.find((v) => v.name === 'Submit Application');
            updateRoute(applicationPage?.link || '/');
        } else if (previousPage) {
            updateRoute(previousPage.link);
        }
    }, [previousPage, pagesOrder, router.query.ref]);

    return {
        goToPreviousPage,
        goToNextPage,
        goToOTPVerification,
        nextPage: nextPage?.name,
        previousPage: previousPage?.name,
        pagesOrder,
        updateRoute,
    };
};
